import _ from 'lodash'
import { openDialog, openErrorDialog } from 'src/redux/slices/alertDialog'
import { stopLoading } from 'src/redux/slices/loading'
import {
  getCourseClassDetail,
  getEnrollQueuePosition,
} from 'src/services/class/detail'
import { ENROLL_QUEUE_KEY } from './model'
import {
  resumeQueueWaiting,
  setQueueRemaining,
  showQueuePopup,
  startQueueWaiting,
  stopQueueWaiting,
} from './index'

// สถานะที่แปลว่าใบสมัครของเราเข้าระบบแล้ว ไม่ต้องรอคิวต่อ
const REGISTERED_STATUS = ['WAITING_RESULT', 'REGISTERED']

// เกณฑ์เดียวกับหน้าล็อกอิน คิวต่ำกว่านี้ยังไม่ต้องโชว์หน้าเข้าแถว
export const getQueueThreshold = () =>
  Number(window?.__env__?.REACT_APP_QUEUE_THRESHOLD) || 0

// คิวสั้นแต่ผลไม่กลับมาเกินเวลานี้ = เปิด popup ให้ผู้ใช้เห็นว่ารออะไรอยู่
// ไม่งั้นจะค้างที่ spinner เงียบ ๆ แบบไม่มีทางออก (เคสที่เจอบน UAT)
const SHOW_POPUP_AFTER_MS = 15000

export const readStoredQueue = () => {
  try {
    return JSON.parse(window.localStorage.getItem(ENROLL_QUEUE_KEY)) || null
  } catch (e) {
    return null
  }
}

// res = ผลของ postEnrollClassQueue -> { ticketId, position }
export const handleEnterQueue =
  (res, trainingPlanUuid, courseUuid = null) =>
  (dispatch) => {
    const ticketId = _.get(res, 'payload.data.ticketId', null)
    const position = Number(_.get(res, 'payload.data.position', 0)) || 0
    if (!ticketId) return

    // เฝ้าบัตรทุกใบ ต่างกันแค่ว่าจะโชว์ popup ทันทีหรือเฝ้าเงียบไว้ก่อน
    const showNow = position >= getQueueThreshold() && position > 0
    if (showNow) dispatch(stopLoading())
    dispatch(
      startQueueWaiting({
        ticketId,
        total: position,
        trainingPlanUuid,
        courseUuid,
        showNow,
      })
    )
  }

/**
 * เปิดหน้าคลาสที่เคยเข้าคิวไว้ -> เด้งกล่องรอคิวให้เหมือนเดิม
 * socket ใบเก่าตายไปพร้อมหน้าเดิมแล้ว ผลจึงส่งกลับมาทาง socket ไม่ได้ ต้องถามสถานะเอง
 */
export const handleResumeQueue = (params) => async (dispatch) => {
  const { uuid, trainingPlanUuid } = params
  const stored = readStoredQueue()
  if (!stored || stored.trainingPlanUuid !== trainingPlanUuid) return

  const isDone = await dispatch(checkQueueDone({ uuid, trainingPlanUuid }))
  if (isDone) return

  dispatch(
    resumeQueueWaiting({
      ticketId: stored.ticketId,
      total: stored.total,
      remaining: stored.remaining,
      trainingPlanUuid,
      courseUuid: uuid,
      startedAt: stored.startedAt,
    })
  )

  // เลขที่เก็บไว้ค้างตั้งแต่ตอนออกจากหน้า ระหว่างนั้นคิวเดินไปเท่าไรไม่มีใครหักออก
  dispatch(refreshQueuePosition())
}

/**
 * งานของเราถูกดำเนินการแล้ว - ปิดกล่องรอคิวแล้วบอกผลตามสถานะจริงของคลาส
 * ทาง socket จะขึ้น dialog ให้เองอยู่แล้ว ทางนี้ไว้รองรับเคสที่ socket ส่งไม่ถึง
 * (รีเฟรช/เปิดใหม่แล้ว socket.id เปลี่ยน หรือ worker คนละเวอร์ชันเป็นคนทำ)
 */
export const handleQueueFinished =
  (detail = null, params = null) =>
  async (dispatch, getState) => {
    // ตอนเปิดหน้ามาใหม่ state ยังไม่ถูกตั้ง (เช็คสถานะก่อน resume) จึงต้องรับ params มาด้วย
    const fromState = _.get(getState(), 'queue', {})
    const trainingPlanUuid =
      _.get(params, 'trainingPlanUuid') || fromState.trainingPlanUuid
    const courseUuid = _.get(params, 'uuid') || fromState.courseUuid
    dispatch(stopQueueWaiting())
    if (!trainingPlanUuid) return

    // ถ้าคนเรียกส่งข้อมูลคลาสมาให้แล้วก็ไม่ต้องถามซ้ำ (query นี้หนัก)
    let data = detail
    if (!data && courseUuid) {
      const res = await dispatch(
        getCourseClassDetail({ uuid: courseUuid, trainingPlanUuid })
      )
      data = _.get(res, 'payload.data', null)
    }

    dispatch(stopLoading())
    const canRegister = _.get(data, 'trainingPlanData.canRegister', '')
    const requireDocument = _.get(data, 'courseDetail.requireDocument', false)

    if (!REGISTERED_STATUS.includes(canRegister)) {
      // คลาสที่ต้องแนบเอกสาร: ระบบสร้างใบสมัครยังไม่ได้จนกว่าจะอัปโหลดครบ
      if (requireDocument) {
        window.location.href = `/upload-document/${trainingPlanUuid}`
        return
      }
      // นอกนั้นบอกกลาง ๆ ให้ไปดูสถานะเอง ดีกว่าเงียบหรือเดาผลผิด
      dispatch(
        openErrorDialog({
          title: 'ยังไม่พบผลการสมัคร',
          message: [
            'ระบบดำเนินการคำขอของท่านแล้วแต่ยังไม่พบใบสมัคร',
            'กรุณาตรวจสอบที่เมนู "คลาสของฉัน" หรือสมัครใหม่อีกครั้ง',
          ],
        })
      )
      return
    }

    dispatch(
      openDialog({
        isCloseDialog: false,
        iconType: 'time',
        title: 'รอผลการตรวจสอบ',
        message: [
          'ระบบได้ทำการส่งอีเมลยืนยันการลงทะเบียนให้ท่านเรียบร้อยแล้ว',
          'โปรดติดตามผลการตรวจสอบเอกสารทางอีเมลอีกครั้ง',
        ],
        buttonRight: {
          label: 'ไปที่คลาสเรียน',
          handleClick: () =>
            (window.location.href = `/my-class/registration-detail/${trainingPlanUuid}`),
        },
        buttonLeft: {
          label: 'ดูหลักสูตรอื่นต่อ',
          handleClick: () => (window.location.href = '/course'),
        },
      })
    )
  }

/** ถามลำดับจริงจากเลขบัตร แล้วเอามาแทนเลขที่ค้างอยู่ */
export const refreshQueuePosition = () => async (dispatch, getState) => {
  const ticketId = _.get(getState(), 'queue.ticketId', null)
  if (!ticketId) return

  const res = await dispatch(getEnrollQueuePosition(ticketId))
  const status = _.get(res, 'payload.data.status', null)

  // ถามไม่ได้ (เน็ตหลุด/เซิร์ฟเวอร์ตอบไม่ได้) - ไม่ตัดสินอะไร รอรอบถัดไป
  if (!status) return

  // ไม่มีบัตรใบนี้ในระบบ (ถูกล้าง หรือคนละ env) - หมุนต่อไม่มีความหมาย ปิดทิ้ง
  if (status === 'NOT_FOUND') {
    dispatch(stopQueueWaiting())
    return
  }

  // บัตรยังไม่ถูกทำและเก่าเกินหน้าต่าง = ไม่มีใครหยิบไปทำ (worker ตาย/ข้อความหาย)
  // ปล่อยให้หมุนต่อไม่มีประโยชน์ ปิดกล่องแล้วบอกให้ไปตรวจสอบ
  if (_.get(res, 'payload.data.isStale', false)) {
    dispatch(stopQueueWaiting())
    dispatch(
      openErrorDialog({
        title: 'ระบบใช้เวลานานกว่าปกติ',
        message: [
          'คำขอสมัครของท่านยังไม่ได้รับการดำเนินการ',
          'กรุณาตรวจสอบที่เมนู "คลาสของฉัน" หรือสมัครใหม่อีกครั้ง',
        ],
      })
    )
    return
  }

  // บัตรถูกปิดแล้ว = worker ทำใบเราเสร็จ ต้องบอกผลให้ผู้ใช้รู้ ไม่ใช่ปิดกล่องแล้วหายเงียบ
  if (status === 'DONE') {
    dispatch(setQueueRemaining(0))
    dispatch(handleQueueFinished())
    return
  }

  // ทำไม่สำเร็จ (เช่น register พัง) - ปิดกล่องแล้วบอกให้รู้ ดีกว่าปล่อยให้หมุนค้าง
  if (status === 'FAILED') {
    dispatch(stopQueueWaiting())
    dispatch(
      openErrorDialog({
        title: 'สมัครเข้าอบรมไม่สำเร็จ',
        message: ['ระบบดำเนินการคำขอของท่านไม่สำเร็จ', 'กรุณาสมัครใหม่อีกครั้ง'],
      })
    )
    return
  }
  dispatch(setQueueRemaining(_.get(res, 'payload.data.position', 0)))

  // เฝ้าเงียบอยู่แต่รอนานเกินไป (ผลไม่กลับมา) - เปิดให้เห็น พร้อมปลด spinner ที่ค้าง
  const { isWaiting, startedAt } = _.get(getState(), 'queue', {})
  if (!isWaiting && startedAt && Date.now() - startedAt > SHOW_POPUP_AFTER_MS) {
    dispatch(stopLoading())
    dispatch(showQueuePopup())
  }
}

// true = ถึงคิวแล้ว (ใบสมัครเข้าระบบเรียบร้อย) พร้อมล้างคิวทิ้ง
export const checkQueueDone = (params) => async (dispatch) => {
  const res = await dispatch(getCourseClassDetail(params))
  const canRegister = _.get(
    res,
    'payload.data.trainingPlanData.canRegister',
    ''
  )
  const isDone = REGISTERED_STATUS.includes(canRegister)
  if (isDone) {
    dispatch(handleQueueFinished(_.get(res, 'payload.data', null), params))
  }
  return isDone
}
